import {
  AnimatePresence,
  motion,
  type PanInfo,
  type TapInfo,
} from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import Matic360Viewer from "@/components/Matic360View/Matic360Viewer";
import turntableFrames from "@/components/Matic360View/frames";
import IconButton from "@/blueprint/components/IconButton/IconButton";
import PauseIcon from "@/blueprint/components/IconButton/PauseIcon";
import PlayIcon from "@/blueprint/components/IconButton/PlayIcon";
import Text from "@/blueprint/components/Text/Text";
import VisuallyHidden from "@/blueprint/components/VisuallyHidden/VisuallyHidden";
import { seconds } from "@/blueprint/tokens/Motion";
import { type ModelVisualOrderVariant, useTrack } from "@/analytics/events";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { swipeStep } from "@/lib/momentum";
import { optimizedPoster } from "@/lib/optimizedPoster";
import { shopifyImageUrl } from "@/lib/shopifyImage";
import { cn } from "@/lib/utils";
import type { FloorPackageId } from "@/views/shop/floorPackages";
import ConfiguratorGallery from "@/views/shop/sections/ConfiguratorGallery";
import {
  ClipSequence,
  FeaturePlayer,
  playableClips,
  FeatureTabBar,
  SlideTabBar,
  benefitTab,
  featureFade,
  useVideoPlayback,
} from "@/views/shop/sections/configurator/FeatureTabs";
import { IMAGE_COLUMN_FRAME } from "@/views/shop/sections/configurator/imageColumn";
import { useTurntableClearance } from "@/views/shop/sections/configurator/useTurntableClearance";
import {
  CONFIGURATOR,
  CONFIGURATOR_VISUALS,
  type ConfiguratorBlockId,
  type ConfiguratorSlideId,
  MATIC_GALLERY,
  type MaticModelId,
  type FilmMoment,
  OVERVIEW_FILM,
} from "@/views/shop/shopContent";
import {
  ADD_ON_CLIPS,
  FEATURE_TABS,
  featureTabsFor,
  type FeatureTabId,
} from "@/views/shop/featureTabsContent";

/**
 * A slide the zone should turn to out of scroll order (a package picked, an
 * add-on focused); `key` tells repeats apart.
 */
export type SlideFocus = { slideId: ConfiguratorSlideId; key: number };

// A focus survives a block change this soon after it: the tap that set it
// often scrolls the page, and the observer reports that a beat later.
const FOCUS_SETTLE_MS = 600;
// How far up, in px, the page must scroll to count as heading back to the
// models (past a layout nudge or a trackpad's settle).
const SCROLL_UP_RELEASE = 24;
// The most a finger may travel, in px, for a press on the media to count as
// a tap (pausing it) rather than a swipe.
const TAP_SLOP = 10;

const STILL_SIZES = "(min-width: 1280px) 70vw, 100vw";
// Between blocks' sets, and on a model switch, the zone crossfades in 0.3s,
// a 4px blur bridging the two frames so they read as one changing rather
// than two overlapping.
const CROSSFADE_MS = 300;
let turntableWarmed = false;
/**
 * Fetches the 360 turntable's rendered frames ahead of its slide, once per
 * page load, so it turns as soon as it comes up.
 */
const warmTurntable = () => {
  if (turntableWarmed) return;
  turntableWarmed = true;
  for (const src of turntableFrames) {
    const image = new Image();
    image.src = src;
  }
};
// A tab or slide picked in order slides in from its side and pushes the
// last one out the other, the way a carousel moves; a critically damped
// spring (Apple's default: no overshoot), so it can be re-aimed mid-slide.
// 0.35s: tabs change often, and a slide that lingers makes them feel slow.
const SLIDE_SPRING = { type: "spring", bounce: 0, duration: 0.35 } as const;
const ZONE_SLIDE = {
  enter: (direction: number) =>
    direction === 0
      ? { opacity: 0, x: 0, filter: "blur(4px)" }
      : { opacity: 1, x: `${direction * 100}%`, filter: "blur(0px)" },
  center: { opacity: 1, x: 0, filter: "blur(0px)" },
  exit: (direction: number) =>
    direction === 0
      ? { opacity: 0, x: 0, filter: "blur(4px)" }
      : { opacity: 1, x: `${direction * -100}%`, filter: "blur(0px)" },
};

/** The pause button as tall as the tab bar, not the full button height:
 *  the tab's line height, its py-xs and the bar's p-1. */
const PAUSE_SIZE = "size-[calc(var(--text-paragraph-sm--line-height)+24px)]";

/**
 * Product shots sit in a square centred on the frame (a size container): as
 * big as fits below `sd`, so the whole product shows (contain), and as big
 * as covers from `sd`, so the frame fills and the edges crop (cover).
 */
const STILL_BOX =
  "absolute left-1/2 top-1/2 size-[min(100cqw,100cqh)] -translate-x-1/2 -translate-y-1/2 bg-surface-page sd:size-[max(100cqw,100cqh)]";

/** Slides that play a film or loop, which the zone's pause button holds. */
const MOTION_SLIDES = new Set<ConfiguratorSlideId>([
  "overview-film",
  "builder-kit",
  "annual-bag-pass",
  "matic-care",
]);

const isModelBlock = (blockId: ConfiguratorBlockId) =>
  blockId === "title" || blockId === "model";

/** The block a slide belongs to, whose set a focus on it shows. */
const homeBlockOf = (slideId: ConfiguratorSlideId): ConfiguratorBlockId =>
  CONFIGURATOR_VISUALS.blocks.find(({ slides }) =>
    (slides as readonly ConfiguratorSlideId[]).includes(slideId),
  )?.id ?? "model";

/**
 * A block's set: its slides that exist (Pet Brush Roll's needs its Shopify
 * photo). A block left with none keeps the set of the block before.
 */
const setFor = (
  blockId: ConfiguratorBlockId,
  petBrushRollImageUrl: string | null,
): readonly ConfiguratorSlideId[] => {
  const blocks = CONFIGURATOR_VISUALS.blocks;
  const start = blocks.findIndex(({ id }) => id === blockId);

  for (let index = start; index >= 0; index -= 1) {
    const slides = (
      blocks[index]?.slides ?? ([] as readonly ConfiguratorSlideId[])
    ).filter(
      (slideId) =>
        slideId !== "pet-brush-roll" || petBrushRollImageUrl !== null,
    );
    if (slides.length > 0) return slides;
  }

  return ["overview-film"];
};

/**
 * A film or loop in the zone, silent and looping, that the zone's pause
 * button holds: it plays unless paused (reduced motion starts paused).
 */
const ZoneVideo = ({
  src,
  poster,
  alt,
  paused,
  onTime,
  className,
}: {
  src: string;
  poster: string;
  /** Null for a loop its slide already names, kept from assistive tech. */
  alt: string | null;
  paused: boolean;
  /** Told the playhead, in seconds, for a running caption; null for none. */
  onTime: ((seconds: number) => void) | null;
  className: string;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useVideoPlayback(videoRef, !paused, src);
  return (
    <video
      ref={videoRef}
      src={src}
      poster={poster}
      preload="metadata"
      autoPlay={!paused}
      loop
      muted
      playsInline
      aria-label={alt ?? undefined}
      aria-hidden={alt === null ? true : undefined}
      onTimeUpdate={
        onTime === null
          ? undefined
          : (event) => onTime(event.currentTarget.currentTime)
      }
      className={cn("pointer-events-none", className)}
    />
  );
};

const SlideContent = ({
  slideId,
  petBrushRollImageUrl,
  floorPackageId,
  onFilmTime,
  paused,
  turntableClearance,
}: {
  slideId: ConfiguratorSlideId;
  petBrushRollImageUrl: string | null;
  floorPackageId: FloorPackageId;
  /** The Overview film's playhead, in seconds, for its running caption. */
  onFilmTime: (seconds: number) => void;
  /** Held by the zone's pause button; every film and clip stops. */
  paused: boolean;
  /**
   * Px from the zone's foot to the robot's lowest point: the hint and tabs
   * below it, and the hint's gap to the tabs again above it. Null until
   * measured.
   */
  turntableClearance: number | null;
}) => {
  switch (slideId) {
    case "orbit":
      return (
        <div className="absolute inset-0 bg-surface-page">
          {/* The whole robot, trimmed of the empty band under it and
              standing on the box's foot, which ends above the hint as far
              as the hint ends above the tabs: the hint centred between. */}
          <div
            className="absolute inset-x-0 top-0"
            style={{ bottom: turntableClearance ?? 0 }}
          >
            <Matic360Viewer enabled hint={false} fit="stand" />
          </div>
        </div>
      );
    case "overview-film":
      return (
        <ZoneVideo
          src={OVERVIEW_FILM.video}
          poster={optimizedPoster(OVERVIEW_FILM.poster.src, 1920)}
          alt={OVERVIEW_FILM.alt}
          paused={paused}
          onTime={onFilmTime}
          className="absolute inset-0 size-full object-cover"
        />
      );
    case "setup":
      // The live renders; the robot count animates as the combination
      // changes. Serene uses the Matic renders (the same robot).
      return <ConfiguratorGallery floorPackageId={floorPackageId} />;
    case "annual-bag-pass":
    case "matic-care":
      return <ClipSequence clips={ADD_ON_CLIPS[slideId]} paused={paused} />;
    case "builder-kit":
      return (
        <div className={STILL_BOX}>
          {/* The slide's label names it; the silent loop adds nothing to read. */}
          <ZoneVideo
            src={CONFIGURATOR_VISUALS.builderKit.video}
            poster={optimizedPoster(
              CONFIGURATOR_VISUALS.builderKit.poster.src,
              1920,
            )}
            alt={null}
            paused={paused}
            onTime={null}
            className="size-full object-contain mix-blend-multiply"
          />
        </div>
      );
    case "pet-brush-roll":
      return petBrushRollImageUrl === null ? null : (
        <div className={STILL_BOX}>
          <img
            src={shopifyImageUrl(petBrushRollImageUrl, 1600)}
            alt={CONFIGURATOR_VISUALS.petBrushRollAlt}
            draggable={false}
            className="pointer-events-none size-full select-none object-contain mix-blend-multiply"
          />
        </div>
      );
  }
};

/**
 * The configurator's visual zone, after Tesla's design studio: the options
 * block crossing the trigger line picks the set (CONFIGURATOR_VISUALS), the
 * zone crossfades to it and opens on its first slide, and arrows (pointer),
 * swipes (touch) and the tab bar move within it.
 * A focus (a package picked, an add-on opened) turns the zone to that
 * slide's own set until the active block next changes, when scroll takes
 * over again. All of it derives from the active block and the selections,
 * so scrolling back up always restores the earlier block's set.
 */
const MaticGallery = ({
  activeBlock,
  modelId,
  floorPackageId,
  petBrushRollImageUrl,
  focus,
  tabId,
  onTabChange,
  clipIndex,
  onClipAdvance,
  quietChanged,
  variant,
  className,
}: {
  activeBlock: ConfiguratorBlockId;
  modelId: MaticModelId;
  floorPackageId: FloorPackageId;
  /** Pet Brush Roll's first Shopify photo; null drops its slide. */
  petBrushRollImageUrl: string | null;
  /** The slide last asked for out of scroll order, shown in its own set. */
  focus: SlideFocus | null;
  /** The feature tab, owned by the page. */
  tabId: FeatureTabId;
  onTabChange: (id: FeatureTabId) => void;
  /** The benefit tab's clip on screen, which its caption names. */
  clipIndex: number;
  onClipAdvance: () => void;
  /** True for a moment after a model switch: the quiet tab's dot. */
  quietChanged: boolean;
  /** The `model-visual-order` arm, which the slide views report. */
  variant: ModelVisualOrderVariant;
  className?: string;
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const track = useTrack();

  // The slide on screen and the set it's in, held for the block that was
  // active when it was chosen; a different block opens on its own first.
  const [view, setView] = useState<{
    anchor: ConfiguratorBlockId;
    block: ConfiguratorBlockId;
    slideId: ConfiguratorSlideId;
    /** Which way it was paged to; 0 for a set changing under it. */
    direction: -1 | 0 | 1;
  } | null>(null);
  // True for a moment after a focus, while the scroll its tap caused lands.
  const settlingRef = useRef(false);
  const held =
    view !== null &&
    (view.anchor === activeBlock || settlingRef.current) &&
    setFor(view.block, petBrushRollImageUrl).includes(view.slideId)
      ? view
      : null;
  const shownBlock = held?.block ?? activeBlock;
  // Matic Design puts the 360 turntable in the zone for the title and model
  // blocks, in place of the Overview film.
  const designShown = isModelBlock(shownBlock) && tabId === "design";
  const slides: readonly ConfiguratorSlideId[] = designShown
    ? ["orbit"]
    : setFor(shownBlock, petBrushRollImageUrl);
  const slideId = designShown
    ? "orbit"
    : (held?.slideId ?? slides[0] ?? "overview-film");
  const index = Math.max(slides.indexOf(slideId), 0);
  // Matic Design opens on the turntable, with no still before it, so its
  // frames load ahead of it once the shopper heads its way: its tab
  // pointed at or focused, or the tab beside it showing.
  const tabOrder = featureTabsFor(modelId).map((tab) => tab.id);
  const designNext =
    isModelBlock(shownBlock) &&
    Math.abs(tabOrder.indexOf(tabId) - tabOrder.indexOf("design")) === 1;
  useEffect(() => {
    if (designNext) warmTurntable();
  }, [designNext]);
  const direction = held?.direction ?? 0;
  // A benefit tab takes the zone over the Overview's renders, for the title
  // and model blocks only; the rest keep following the scroll.
  const featureShown =
    isModelBlock(shownBlock) && tabId !== "overview" && tabId !== "design";
  const feature = featureShown ? benefitTab(modelId, tabId) : null;
  // The caption over the tabs, Tesla's: a film's runs with it, a line per
  // shot (the Overview film, or a tab's clip that carries its moments, as
  // Cleaning's film); other clips show their bullet, else the tab's title.
  // A new film starts its clock again.
  const featureClip =
    feature === null ? undefined : playableClips(feature)[clipIndex];
  const clipMoments =
    featureClip?.kind === "video" ? featureClip.moments : null;
  const filmKey =
    feature === null ? slideId : `feature:${feature.id}:${featureClip?.id}`;
  const [filmTime, setFilmTime] = useState(0);
  const [filmFor, setFilmFor] = useState(filmKey);
  if (filmFor !== filmKey) {
    setFilmFor(filmKey);
    setFilmTime(0);
  }
  const film =
    feature === null && slideId === "overview-film" ? OVERVIEW_FILM : null;
  // The pause button holds whatever plays in the zone; a new tab or slide
  // starts playing again (reduced motion: starts paused, the shopper plays).
  // The preference counts too: on a fresh load it reads as reduced until the
  // browser answers, and the film must start once it says otherwise.
  const motionKey =
    feature !== null
      ? `feature:${feature.id}:${modelId}`
      : `${modelId}:${slideId}`;
  const pauseKey = `${motionKey}:${reducedMotion}`;
  const [paused, setPaused] = useState(reducedMotion);
  const [pausedFor, setPausedFor] = useState(pauseKey);
  if (pausedFor !== pauseKey) {
    setPausedFor(pauseKey);
    setPaused(reducedMotion);
  }
  const hasMotion = feature !== null || MOTION_SLIDES.has(slideId);
  const togglePaused = () => {
    track("video_toggled", { is_playing: paused });
    setPaused(!paused);
  };
  // A tab bar runs along the foot from `tab`: the model block's feature
  // tabs, or the add-ons as tabs.
  const hasFootBar =
    isModelBlock(shownBlock) ||
    (shownBlock === "addOns" && feature === null && slides.length > 1);
  const pauseButton = (className: string) => (
    <IconButton
      label={
        paused
          ? CONFIGURATOR_VISUALS.videoToggle.play
          : CONFIGURATOR_VISUALS.videoToggle.pause
      }
      onClick={togglePaused}
      className={className}
    >
      {paused ? (
        <PlayIcon className="size-m" />
      ) : (
        <PauseIcon className="size-m" />
      )}
    </IconButton>
  );
  // As on Tesla's, a tap or click on the playing media pauses and resumes it
  // beside the pause button. A finger that travels is a swipe or a scroll,
  // never a tap.
  const tapStartRef = useRef<{ x: number; y: number } | null>(null);
  const handleMediaTapStart = (_event: unknown, info: TapInfo) => {
    tapStartRef.current = info.point;
  };
  const handleMediaTap = (
    event: MouseEvent | TouchEvent | PointerEvent,
    info: TapInfo,
  ) => {
    const start = tapStartRef.current;
    tapStartRef.current = null;
    if (!hasMotion || reducedMotion || start === null) return;
    if (Math.hypot(info.point.x - start.x, info.point.y - start.y) > TAP_SLOP)
      return;
    if (
      event.target instanceof Element &&
      event.target.closest("button, a") !== null
    )
      return;
    togglePaused();
  };
  const momentAt = (moments: readonly FilmMoment[]) =>
    moments.findLast(({ from }) => from <= filmTime)?.text ?? null;
  const caption =
    feature !== null
      ? clipMoments !== null
        ? momentAt(clipMoments)
        : (feature.bullets.find(({ clipId }) => clipId === featureClip?.id)
            ?.text ?? feature.title)
      : film === null
        ? null
        : momentAt(film.moments);
  // The turntable's clearance (SlideContent), measured off the foot.
  const footRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLParagraphElement>(null);
  const footBarRef = useRef<HTMLDivElement>(null);
  const turntableClearance = useTurntableClearance(
    { foot: footRef, hint: hintRef, bar: footBarRef },
    designShown,
  );
  // The arrows step through the feature tabs on the title and model blocks,
  // and through the slides (the add-ons) everywhere else.
  const featureTabs = featureTabsFor(modelId);
  const tabIds = featureTabs.map(({ id }) => id);
  const stepsTabs = isModelBlock(shownBlock);
  const step = (direction: -1 | 1) => {
    if (!stepsTabs) {
      goTo(index + direction);
      return;
    }
    const next =
      tabIds[
        (tabIds.indexOf(tabId) + direction + tabIds.length) % tabIds.length
      ];
    if (next === undefined) return;
    setArrowDirection(direction);
    onTabChange(next);
  };

  // Which way the zone slides when its tab changes: the arrow's way, else
  // the way along the tab bar a pick lies. A scroll to another block or a
  // model switch has no direction, and crossfades.
  const [arrowDirection, setArrowDirection] = useState<-1 | 1 | null>(null);
  const [tabMotion, setTabMotion] = useState({
    tab: tabId,
    block: shownBlock,
    model: modelId,
    direction: 0 as -1 | 0 | 1,
  });
  if (
    tabMotion.tab !== tabId ||
    tabMotion.block !== shownBlock ||
    tabMotion.model !== modelId
  ) {
    const byTab =
      tabMotion.tab !== tabId &&
      tabMotion.block === shownBlock &&
      tabMotion.model === modelId;
    setTabMotion({
      tab: tabId,
      block: shownBlock,
      model: modelId,
      direction: byTab
        ? (arrowDirection ??
          (Math.sign(tabIds.indexOf(tabId) - tabIds.indexOf(tabMotion.tab)) as
            -1 | 0 | 1))
        : 0,
    });
    setArrowDirection(null);
  }
  const slideDirection = reducedMotion
    ? 0
    : isModelBlock(shownBlock)
      ? tabMotion.direction
      : direction;

  const goTo = (nextIndex: number) => {
    const next = slides[(nextIndex + slides.length) % slides.length];
    if (next !== undefined)
      setView({
        anchor: activeBlock,
        block: shownBlock,
        slideId: next,
        direction: nextIndex >= index ? 1 : -1,
      });
  };

  const handleDragEnd = (_event: unknown, info: PanInfo) => {
    if (slides.length < 2) return;
    const swipe = swipeStep(info.offset.x, info.velocity.x);
    if (swipe !== 0) goTo(index + swipe);
  };

  // A focus turns the zone to its slide's own set, in place: a package
  // picked shows the renders counting robots, an add-on its imagery.
  const activeBlockRef = useRef(activeBlock);
  activeBlockRef.current = activeBlock;
  useEffect(() => {
    if (focus === null) return;
    setView({
      anchor: activeBlockRef.current,
      block: homeBlockOf(focus.slideId),
      slideId: focus.slideId,
      direction: 0,
    });
    settlingRef.current = true;
    const settle = window.setTimeout(() => {
      settlingRef.current = false;
    }, FOCUS_SETTLE_MS);
    return () => window.clearTimeout(settle);
  }, [focus]);

  // A block change while settling moves the focus's anchor along with it,
  // so the focus holds until the next deliberate scroll.
  useEffect(() => {
    if (!settlingRef.current) return;
    setView((current) =>
      current === null || current.anchor === activeBlock
        ? current
        : { ...current, anchor: activeBlock },
    );
  }, [activeBlock]);

  // A package or add-on picked while the model block is still the active
  // one is anchored to it, so leaving that block never releases it. Heading
  // back up is a deliberate return to the models: once settled, a scroll up
  // with a model block active lets the held set go, and the zone shows the
  // model block's own visual again.
  useEffect(() => {
    let lastY = window.scrollY;
    const handleScroll = () => {
      const y = window.scrollY;
      const isUp = y < lastY - SCROLL_UP_RELEASE;
      if (y > lastY || isUp) lastY = y;
      if (!isUp || settlingRef.current) return;
      if (!isModelBlock(activeBlockRef.current)) return;
      setView((current) =>
        current === null || isModelBlock(current.block) ? current : null,
      );
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // A model or set switch always shows: the zone returns to the model
  // set's first slide, which crossfades since every slide is keyed by its
  // model too. Reset during render, so no frame ever pairs the new choice
  // with the old slide (a stray crossfade and a slide view nobody saw).
  const choice = modelId;
  const [shownChoice, setShownChoice] = useState(choice);
  if (shownChoice !== choice) {
    setShownChoice(choice);
    const first = setFor("model", petBrushRollImageUrl)[0];
    if (first !== undefined)
      setView({
        anchor: activeBlock,
        block: "model",
        slideId: first,
        direction: 0,
      });
  }
  // The tap on a model card may scroll the page; hold through it.
  const isFirstModelRef = useRef(true);
  useEffect(() => {
    if (isFirstModelRef.current) {
      isFirstModelRef.current = false;
      return;
    }
    settlingRef.current = true;
    const settle = window.setTimeout(() => {
      settlingRef.current = false;
    }, FOCUS_SETTLE_MS);
    return () => window.clearTimeout(settle);
  }, [modelId]);

  // Each carousel slide that comes up, however it got there, with the model
  // and the experiment arm; benefit tabs report as visual_tab_selected.
  useEffect(() => {
    if (featureShown) return;
    track("visual_slide_viewed", {
      model: modelId,
      block_id: shownBlock,
      slide_id: slideId,
      variant,
    });
  }, [track, featureShown, modelId, shownBlock, slideId, variant]);

  // Announced politely when the set changes block, never per scroll tick.
  const [announcement, setAnnouncement] = useState("");
  const firstRenderRef = useRef(true);
  const announce = CONFIGURATOR_VISUALS.blocks.find(
    ({ id }) => id === shownBlock,
  )?.announce;
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (announce !== undefined) setAnnouncement(announce);
  }, [announce]);

  const labelOf = (item: ConfiguratorSlideId) =>
    CONFIGURATOR_VISUALS.slideLabels[item];

  const fade = reducedMotion
    ? { duration: 0 }
    : { duration: seconds(CROSSFADE_MS), ease: "easeOut" as const };

  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={fade}
      role="region"
      aria-roledescription="carousel"
      aria-label={MATIC_GALLERY.label}
      // The page's ground, so options scrolling under the pinned zone never
      // show around its stills.
      className={cn(IMAGE_COLUMN_FRAME, "isolate bg-surface-page", className)}
    >
      <AnimatePresence initial={false} custom={slideDirection}>
        {feature === null ? (
          <motion.div
            // Keyed by slide and model: a set that opens on the same slide
            // as the last (Title to Model, Combination to Checkout) holds
            // still, and a model switch always crossfades.
            key={`${modelId}:${slideId}`}
            role="group"
            aria-roledescription="slide"
            aria-label={labelOf(slideId)}
            custom={slideDirection}
            variants={ZONE_SLIDE}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideDirection === 0 ? fade : SLIDE_SPRING}
            // The turning render takes its own drag; it pages by arrow.
            drag={slides.length > 1 && slideId !== "orbit" ? "x" : false}
            // A vertical swipe stays the page's scroll, never the carousel's.
            dragDirectionLock
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={handleDragEnd}
            onTapStart={handleMediaTapStart}
            onTap={handleMediaTap}
            className="absolute inset-0 touch-pan-y"
          >
            <SlideContent
              slideId={slideId}
              petBrushRollImageUrl={petBrushRollImageUrl}
              floorPackageId={floorPackageId}
              onFilmTime={setFilmTime}
              paused={paused}
              turntableClearance={turntableClearance}
            />
          </motion.div>
        ) : (
          <motion.div
            // The shared tabs keep one key across models, so a switch only
            // updates their card in place; the quiet tab's is per model, so
            // its media and card crossfade.
            key={
              feature.id === "quiet"
                ? `feature:quiet:${modelId}`
                : `feature:${feature.id}`
            }
            custom={slideDirection}
            variants={ZONE_SLIDE}
            initial="enter"
            animate="center"
            exit="exit"
            transition={
              slideDirection === 0 ? featureFade(reducedMotion) : SLIDE_SPRING
            }
            onTapStart={handleMediaTapStart}
            onTap={handleMediaTap}
            className="absolute inset-0"
          >
            <FeaturePlayer
              tab={feature}
              modelId={modelId}
              clipIndex={clipIndex}
              onAdvance={onClipAdvance}
              sizes={STILL_SIZES}
              paused={paused}
              onTime={setFilmTime}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Arrows centred on the frame's side edges from `sd`, with swipes
          on touch. Below it there are no arrows or dots: the feature tabs
          page the films, along the foot on tablets and as the bar under
          the frame on phones (ShopView), and swipes still turn slides. */}
      {(stepsTabs || slides.length > 1) && (
        <>
          <IconButton
            label={MATIC_GALLERY.previous}
            onClick={() => step(-1)}
            className="absolute left-[24px] top-1/2 hidden -translate-y-1/2 sd:flex"
          >
            <ChevronLeft aria-hidden className="size-l" strokeWidth={1.5} />
          </IconButton>
          <IconButton
            label={MATIC_GALLERY.next}
            onClick={() => step(1)}
            className="absolute right-[24px] top-1/2 hidden -translate-y-1/2 sd:flex"
          >
            <ChevronRight aria-hidden className="size-l" strokeWidth={1.5} />
          </IconButton>
        </>
      )}

      {/* Always in view, so anything that plays can be stopped (WCAG 2.2.2):
          on phones in the top-left corner, as tall as the tab bar; from
          `tab` in the bottom-left corner, or leading the tab bar's row
          (below) where there is one. A tap on the media pauses it too. */}
      {hasMotion &&
        pauseButton(
          cn("absolute left-[12px] top-[12px] z-10 tab:hidden", PAUSE_SIZE),
        )}
      {hasMotion &&
        !hasFootBar &&
        pauseButton(
          "absolute bottom-[12px] left-[12px] z-10 hidden tab:flex sd:bottom-[24px] sd:left-[24px]",
        )}

      {/* A shade along the foot, so a caption and the tabs read over any
          clip or film. */}
      {(feature !== null || film !== null) && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-tint-black-60 to-transparent"
        />
      )}
      <div
        ref={footRef}
        className="pointer-events-none absolute inset-x-0 bottom-[8px] flex flex-col items-center gap-[8px] tab:bottom-[12px] sd:bottom-[24px] [&>*]:pointer-events-auto"
      >
        <AnimatePresence mode="wait" initial={false}>
          {caption !== null && (
            <motion.p
              key={caption}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reducedMotion ? 0 : seconds(200) }}
              // Clear of the tabs: 16px under it in all, 20px from `sd`.
              className="mb-xs max-w-full px-l text-center sd:mb-s"
            >
              {/* A block, so its token's leading sets the lines, not the
                  paragraph's inherited body leading. */}
              <Text
                variant="film-caption"
                tone="inverse"
                as="span"
                className="block"
              >
                {caption}
              </Text>
            </motion.p>
          )}
        </AnimatePresence>
        {/* The turntable's hint: on the page, unshaded, and left off on
            mobile; its slider's label already says so for assistive tech.
            Its own element, so it mounts with the 3D View rather than after
            the last caption fades, and the turntable measures it at once. */}
        {designShown && (
          <motion.p
            ref={hintRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: reducedMotion ? 0 : seconds(200) }}
            aria-hidden
            className="mb-xs hidden max-w-full px-l text-center tab:block sd:mb-s"
          >
            <Text
              variant="turntable-hint"
              tone="secondary"
              as="span"
              className="block"
            >
              {FEATURE_TABS.design.hint}
            </Text>
          </motion.p>
        )}
        {/* The bar along the foot, in its small tabs at every width, the
            pause button leading it in one row so neither covers the other:
            the model block's feature tabs, or the add-ons as tabs. Where it
            can't fit, it scrolls sideways and its hidden side fades. The
            button joins the row from `tab`; on phones it sits top left. */}
        {hasFootBar && (
          <div
            ref={footBarRef}
            className="flex max-w-full items-center gap-xs px-l"
          >
            {hasMotion &&
              pauseButton(cn("hidden shrink-0 tab:flex", PAUSE_SIZE))}
            {isModelBlock(shownBlock) ? (
              <FeatureTabBar
                modelId={modelId}
                activeId={tabId}
                quietChanged={quietChanged}
                onSelect={onTabChange}
                onIntent={(id) => {
                  if (id === "design") warmTurntable();
                }}
                size="sm"
                className="min-w-0"
              />
            ) : (
              <SlideTabBar
                label={CONFIGURATOR.addOnsHeading}
                slides={slides}
                activeId={slideId}
                labelOf={labelOf}
                onSelect={(id) => goTo(slides.indexOf(id))}
                size="sm"
                className="min-w-0"
              />
            )}
          </div>
        )}
      </div>

      <VisuallyHidden>
        <span aria-live="polite">{announcement}</span>
      </VisuallyHidden>
    </motion.div>
  );
};

export default MaticGallery;
